/* ==========================================================================
   nvForceCollectionNavigation
   Boost の Collection タイプ絞り込みを本来のページ遷移に戻す
   File   : assets/nv-boost-nav.js
   Loaded : layout/theme.liquid から defer で読み込む
   Added  : 2026-08-25
   --------------------------------------------------------------------------
   Problem / 症状:
     Clicking バッグ in the sidebar loads the bag products but leaves the
     <title>, the breadcrumb and the collection banner showing ALL ITEM.
     サイドバーで「バッグ」を押すと商品は切り替わるが、<title>・パンくず・
     コレクションバナーは ALL ITEM のまま残る。

   Cause / 原因:
     Boost treats a Collection-type filter like any other filter: it swaps the
     product grid over XHR and rewrites the address bar with history.pushState.
     The document is never re-requested, so everything rendered by Liquid still
     belongs to the page the visitor arrived on.
     Boost は Collection タイプの絞り込みも通常のフィルターと同様に扱い、
     商品グリッドだけを差し替えて history.pushState で URL を書き換える。
     ドキュメントは再取得されないため、Liquid が描画した部分はすべて
     最初に開いたページのまま残る。

     Boost 管理画面にこの挙動を切り替える設定は無い
     （Filter option の General / Advanced 両タブとも該当項目なし・確認済み）。

   Why a separate file / なぜ別ファイルにするか:
     boost-sd-custom.js は Boost 管理画面の Custom JS 欄から保存された際に
     上書きされる可能性がある。アプリが生成するファイルには手を入れない。

   Why match on the href / なぜ href で判定するか:
     - Boost changes its class names without notice.
       Boost のクラス名は予告なく変更される。
     - The label is unreliable: on the ALL ITEM tree「カテゴリ」holds Collection
       links, while on the bag tree the same label holds tag checkboxes. It also
       differs across the six locales.
       ラベルも当てにならない。ALL ITEM のツリーでは「カテゴリ」が Collection
       リンクだが、bag のツリーでは同じラベルがタグのチェックボックスになる。
       6言語で表記も変わる。
     - A bare /collections/<handle> path is a navigation by definition;
       a filter always carries a query string.
       クエリ文字列のない /collections/<handle> は定義上ページ遷移であり、
       フィルターは必ずクエリ文字列を伴う。

   Trade-off / 代償:
     A real navigation is slower than a partial update. In exchange the title,
     breadcrumb, banner and canonical URL are all correct.
     本来の遷移は部分更新より遅い。その代わりタイトル・パンくず・バナー・
     canonical がすべて正しくなる。

   To disable / 無効化する場合:
     Remove the script tag from layout/theme.liquid. Nothing depends on this file.
     theme.liquid の script タグを外す。他の処理はこのファイルに依存しない。
   ========================================================================== */

(function () {
  'use strict';

  /* /collections/handle, allowing a locale prefix such as /en or /zh-CN.
     A query string or a deeper path means it is not a plain collection link.
     /en や /zh-CN のロケール接頭辞を許容しつつ /collections/handle に一致。
     クエリ文字列や階層が深い場合は素のコレクションリンクではない。 */
  var COLLECTION_PATH = /^(?:\/[a-z]{2}(?:-[A-Za-z]{2,4})?)?\/collections\/[^\/?#]+\/?$/;

  /* Theme-owned navigation. These links already behave correctly, and calling
     stopPropagation() on them would break GA4 / Pinterest click tracking and
     the theme's page-transition fade.
     テーマ側のナビゲーション。元から正しく動作しており、ここで
     stopPropagation() すると GA4 / Pinterest の計測とページ遷移フェードが
     止まってしまう。

     .SidebarMenu is the mobile drawer, which sits outside .Header.
     .SidebarMenu はモバイルのドロワーで .Header の外側にあるため別途必要。

     .nv-tabbar はスマホ下部の固定バー（2026-09-29 追加）。これも .Header の
     外側にあるので明示が要る。

     ★ ここに足し忘れると、バーの「カテゴリー」がドロワーを開かずに
       遷移してしまう。原因は下の stopPropagation()：捕獲フェーズで
       伝播を止めるため、テーマの open-drawer ハンドラ（バブリング）に
       届かなくなる。しかも 113 行の「同じコレクション上なら素通し」が
       あるため、商品一覧の上でだけ正しく動き、他のページでだけ壊れる。
       2026-09-29 に実際にこの症状が出た。

     ★ .nv-tabbar is the fixed mobile bottom bar (added 2026-09-29), also
       outside .Header. Forgetting it here makes the bar's category slot
       navigate instead of opening the drawer: the stopPropagation() below
       runs in the capture phase and the theme's bubbling open-drawer handler
       never fires. Worse, the "already on that collection" check on line 113
       means it works on the collection page and breaks everywhere else, which
       is exactly how it showed up. */
  var THEME_NAV = '.Header, .SidebarMenu, .Footer, .nv-tabbar';

  document.addEventListener(
    'click',
    function (event) {
      /* Leave anything another handler already claimed.
         他が処理を宣言済みのものには触れない。 */
      if (event.defaultPrevented) return;

      /* Middle click, right click and modifier clicks open new tabs.
         Hijacking them would break that expectation.
         中クリック・右クリック・修飾キー付きは新規タブを開く操作のため除外。 */
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var target = event.target;
      if (!target || !target.closest) return;

      var link = target.closest('a[href]');
      if (!link) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      /* Theme navigation passes through untouched.
         テーマのナビはそのまま素通しする。 */
      if (link.closest(THEME_NAV)) return;

      var url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (err) {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (!COLLECTION_PATH.test(url.pathname)) return;

      /* Already on that collection - leave it to Boost.
         同じコレクション上なら Boost の処理に任せる。 */
      if (url.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '')) {
        return;
      }

      /* The capture phase runs before Boost's handler, which is bound to the
         React root rather than to document.
         捕獲フェーズは、document ではなく React ルートに束ねられている
         Boost のハンドラより先に実行される。 */
      event.preventDefault();
      event.stopPropagation();

      window.location.href = url.href;
    },
    true
  );
})();